import { useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Button, Card, Chip, ConfirmDeleteModal, EmptyState, Input, Modal, Skeleton } from '../ui'
import { api, ApiError } from '../../api/client'
import { useAxemaDoors, useAxemaPendingControllers } from '../../api/hooks'
import { useAuthStore } from '../../stores/auth'
import { formatMac, normalizeMac, pendingControllerLabel } from '../../lib/mac'
import type { AccessSystem, AxDoor } from '../../api/types'

function errDetail(e: unknown): string {
  if (e instanceof ApiError) return e.detail
  if (e instanceof Error) return e.message
  return 'Request failed'
}

/** The select value for the manual-entry fallback (pending MACs themselves
 *  are 12-hex-digit strings, so this sentinel can never collide). */
const MANUAL = '__manual'

/**
 * Bind/rebind dialog for one door's DCIP controller. Primary path is the
 * pending-controller pick-list (`GET .../pending-controllers`: deduped
 * sightings, bound MACs already excluded server-side); "Enter manually…"
 * keeps the validated free-text fallback for MACs never sighted (or when the
 * pick-list is unavailable — it 403s viewers and is treated as
 * manual-only on any error). Manual input accepts any common MAC format
 * (colons/dashes/dots, any case) — `normalizeMac` mirrors the backend's
 * canonicalization, so a malformed value fails locally with the same message
 * the API's 422 carries. Mounted fresh per open (state initializes from the
 * door's current binding).
 */
export function BindControllerModal({
  systemUid,
  door,
  onClose,
}: {
  systemUid: string
  door: AxDoor
  onClose: () => void
}) {
  const scope = useAuthStore((s) => s.customer?.unique_id ?? 'none')
  const queryClient = useQueryClient()
  const pending = useAxemaPendingControllers(systemUid)
  const [selection, setSelection] = useState<string>(door.mac ? MANUAL : '')
  const [mac, setMac] = useState(door.mac ? formatMac(door.mac) : '')
  const [touched, setTouched] = useState(false)

  const bind = useMutation({
    mutationFn: (normalized: string) => api.bindAxemaDoorController(systemUid, door.unique_id, normalized),
    onSuccess: () => {
      // The bound MAC drops out of the pending list server-side.
      queryClient.invalidateQueries({ queryKey: [scope, 'axema-doors', systemUid] })
      queryClient.invalidateQueries({ queryKey: [scope, 'axema-pending-controllers', systemUid] })
      onClose()
    },
  })

  // No pick-list on error (403 viewer, offline) or on an empty list — the
  // manual input becomes the only path.
  const pickerAvailable = !pending.isError && (pending.data?.length ?? 0) > 0
  const manualActive = !pickerAvailable || selection === MANUAL
  const manualNormalized = normalizeMac(mac)
  /** The canonical 12-hex MAC to submit, null while nothing valid is chosen. */
  const effective = manualActive ? manualNormalized : selection !== '' ? selection : null
  const invalid = manualActive && touched && mac.trim() !== '' && manualNormalized === null

  function submit() {
    if (effective === null || bind.isPending) return
    bind.mutate(effective)
  }

  return (
    <Modal open title={`Controller — ${door.name}`} onClose={onClose}>
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault()
          submit()
        }}
      >
        {pending.isPending ? (
          <Skeleton className="h-9" />
        ) : pickerAvailable ? (
          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="pending-controller"
              className="text-[10px] font-semibold uppercase tracking-widest text-ostrel-ink-muted"
            >
              Pending controller
            </label>
            <select
              id="pending-controller"
              value={selection}
              onChange={(e) => setSelection(e.target.value)}
              autoFocus={!door.mac}
              className="rounded-sm border border-ostrel-line bg-ostrel-card px-3 py-2 text-sm text-ostrel-ink focus:border-ostrel-gold focus:outline-none focus:ring-1 focus:ring-ostrel-gold"
            >
              <option value="">— pick a controller</option>
              {pending.data?.map((p) => (
                <option key={p.mac} value={p.mac} className="font-mono">
                  {pendingControllerLabel(p)}
                </option>
              ))}
              <option value={MANUAL}>Enter manually…</option>
            </select>
          </div>
        ) : (
          <p className="text-[11px] leading-relaxed text-ostrel-ink-muted">
            {pending.isError
              ? `Could not load pending controllers (${errDetail(pending.error)}) — enter the MAC manually.`
              : 'No controllers are pending right now — one appears here when unknown hardware joins the appliance. You can still enter a MAC manually.'}
          </p>
        )}

        {manualActive && !pending.isPending && (
          <Input
            label="Controller MAC"
            value={mac}
            onChange={(e) => {
              setMac(e.target.value)
              setTouched(true)
            }}
            placeholder="aa:bb:cc:dd:ee:ff"
            autoComplete="off"
            autoFocus={!pickerAvailable && !door.mac}
            className="tnum font-mono"
            error={invalid ? 'MAC must be 12 hex digits (separators allowed)' : undefined}
          />
        )}

        <p className="text-[11px] leading-relaxed text-ostrel-ink-muted">
          Pending controllers are DCIP hardware the appliance saw join but has not provisioned yet
          (the worker's <span className="font-mono">controller.pending</span> sightings, deduped —
          already-bound MACs are excluded). The appliance adopts the binding on its next directory
          refresh; the panel (Vaka) knows nothing about it.
        </p>
        {bind.isError && (
          <p className="rounded-sm border border-ostrel-danger/40 bg-ostrel-danger/10 px-3 py-2 text-xs text-ostrel-danger">
            {errDetail(bind.error)}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="ghost" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="navy" type="submit" disabled={bind.isPending || effective === null}>
            {bind.isPending ? 'Binding…' : door.mac ? 'Save binding' : 'Bind controller'}
          </Button>
        </div>
      </form>
    </Modal>
  )
}

/**
 * Door-controller bindings for one Axema system: the AxDoor mirror rows with
 * the operator-bound DCIP controller MAC of each, plus bind/rebind/unbind
 * (operator+ — the API gates on the role and audit-logs every change; a
 * viewer sees the bindings read-only). Rendered only for provider 'axema'
 * (the route 404s for any other system's uid).
 */
export function DoorControllersCard({ system }: { system: AccessSystem }) {
  const canOperate = useAuthStore((s) => s.customer?.role === 'admin' || s.customer?.role === 'operator')
  const scope = useAuthStore((s) => s.customer?.unique_id ?? 'none')
  const queryClient = useQueryClient()
  const isAxema = system.provider === 'axema'
  const doors = useAxemaDoors(isAxema ? system.unique_id : undefined)

  const [bindDoor, setBindDoor] = useState<AxDoor | null>(null)
  const [confirmUnbind, setConfirmUnbind] = useState<AxDoor | null>(null)

  const unbind = useMutation({
    mutationFn: (door: AxDoor) => api.bindAxemaDoorController(system.unique_id, door.unique_id, ''),
    onSuccess: () => {
      // The freed MAC rejoins the pending pick-list (its sightings are kept).
      queryClient.invalidateQueries({ queryKey: [scope, 'axema-doors', system.unique_id] })
      queryClient.invalidateQueries({ queryKey: [scope, 'axema-pending-controllers', system.unique_id] })
      setConfirmUnbind(null)
    },
  })

  // Keep the modal's door row in step with refetched data (a live update can
  // flip the binding while the dialog is open).
  useEffect(() => {
    if (bindDoor && doors.data) {
      const fresh = doors.data.find((d) => d.unique_id === bindDoor.unique_id)
      if (fresh && fresh !== bindDoor) setBindDoor(fresh)
    }
    // doors.data identity changes per fetch; only sync while a dialog is open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doors.data])

  if (!isAxema) return null

  return (
    <Card
      title="Door controllers"
      action={
        doors.data && (
          <span className="tnum text-[11px] text-ostrel-ink-muted">
            {doors.data.filter((d) => d.mac !== '').length} of {doors.data.length} bound
          </span>
        )
      }
    >
      <p className="mb-3 text-[11px] leading-relaxed text-ostrel-ink-muted">
        DCIP controllers (B60 replacements) join the appliance unprovisioned and wait as pending
        until bound to a door here. The binding is a local mirror write — the appliance picks it up
        on its next directory refresh.
      </p>
      {doors.isPending ? (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-11" />
          <Skeleton className="h-11" />
        </div>
      ) : doors.isError ? (
        <EmptyState
          title="Could not load doors"
          hint={errDetail(doors.error)}
          action={
            <Button variant="ghost" size="sm" onClick={() => doors.refetch()}>
              Retry
            </Button>
          }
        />
      ) : doors.data.length === 0 ? (
        <EmptyState
          title="No doors synced yet"
          hint="Doors appear once the connector has synced this system."
        />
      ) : (
        <ul className="flex flex-col gap-2">
          {doors.data.map((d) => (
            <li
              key={d.unique_id}
              className="flex flex-wrap items-center gap-2 rounded-sm border border-ostrel-line bg-ostrel-card px-3 py-2.5"
            >
              <span className="min-w-0">
                <span className="block truncate text-sm font-medium text-ostrel-ink">{d.name}</span>
                <span className="tnum block font-mono text-[11px] text-ostrel-ink-muted">
                  {d.unique_id}
                </span>
              </span>
              {d.not_found && <Chip tone="warn" label="not found" />}
              {d.mac ? (
                <span className="tnum rounded-sm border border-ostrel-line bg-ostrel-sand px-1.5 py-0.5 font-mono text-[11px] text-ostrel-ink">
                  {formatMac(d.mac)}
                </span>
              ) : (
                <Chip tone="muted" label="no controller" />
              )}
              {canOperate && (
                <span className="ml-auto flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={d.not_found}
                    title={d.not_found ? 'The panel no longer reports this door' : undefined}
                    onClick={() => setBindDoor(d)}
                  >
                    {d.mac ? 'Edit' : 'Bind'}
                  </Button>
                  {d.mac !== '' && (
                    <Button variant="danger" size="sm" onClick={() => setConfirmUnbind(d)}>
                      Unbind
                    </Button>
                  )}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}

      {bindDoor && (
        <BindControllerModal
          systemUid={system.unique_id}
          door={bindDoor}
          onClose={() => setBindDoor(null)}
        />
      )}

      <ConfirmDeleteModal
        open={confirmUnbind !== null}
        title="Unbind controller"
        objectName={confirmUnbind?.name ?? ''}
        expected={confirmUnbind?.unique_id ?? ''}
        pending={unbind.isPending}
        error={unbind.error ? errDetail(unbind.error) : null}
        confirmLabel={unbind.isPending ? 'Unbinding…' : 'Unbind'}
        onConfirm={() => confirmUnbind && unbind.mutate(confirmUnbind)}
        onClose={() => setConfirmUnbind(null)}
      >
        <p>
          Unbind controller{' '}
          <span className="tnum font-mono">{confirmUnbind ? formatMac(confirmUnbind.mac) : ''}</span>{' '}
          from <strong>{confirmUnbind?.name}</strong>? The controller goes back to pending at the
          appliance; the door itself is untouched.
        </p>
      </ConfirmDeleteModal>
    </Card>
  )
}
